import { DragEvent, FC, KeyboardEvent, useRef, useState } from 'react'

import styled, { css } from 'styled-components'
import { useMediaQuery } from 'react-responsive'

import UploadFolderIcon from 'src/Assets/Svg/uploadFolder.svg?react'
import { variables, MAX_TABLET_WIDTH } from 'src/Styled/variables'

import { CTAButton } from '../Buttons/CTAButton'
import { GreyTextButton } from '../Buttons/GreyTextButton'

import { AUDIO_FILE_ACCEPT, AUDIO_FILE_FORMATS_HINT } from './validation'

export type AudioFileDropzoneState = 'enabled' | 'replace' | 'error'

export type AudioFileDropzoneProps = {
  state?: AudioFileDropzoneState
  disabled?: boolean
  loading?: boolean
  errorId?: string
  onFilePick: (file: File) => void
}

const DISABLED_TAB_INDEX = -1

const Container = styled.div<{ $dragging: boolean; $invalid: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  min-height: 280px;
  padding: 56px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  cursor: pointer;
  transition: border-color 0.2s;

  ${({ $invalid }) =>
    $invalid &&
    css`
      border-color: ${({ theme }) => theme.color.mediumRed};
    `}

  ${({ $dragging }) =>
    $dragging &&
    css`
      border-color: ${({ theme }) => theme.color.brandGreen};
      background: ${({ theme }) => theme.color.hoverWhite};
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brandGreen};
    outline-offset: 2px;
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`

const IconWrapper = styled.div<{ $dragging: boolean }>`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme, $dragging }) =>
    $dragging ? theme.color.brandGreen : theme.color.darkGrey};
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  text-align: center;
  word-break: break-word;
`

const Title = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize14};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
`

const Hint = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.color.slateGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize12};
  font-weight: ${variables.fontWeight400};
  line-height: ${variables.lineHeight150per};
`

const HiddenInput = styled.input`
  display: none;
`

export const AudioFileDropzone: FC<AudioFileDropzoneProps> = ({
  state = 'enabled',
  disabled,
  loading,
  errorId,
  onFilePick,
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const isTouchLayout = useMediaQuery({
    query: `(max-width: ${MAX_TABLET_WIDTH})`,
  })

  const isReplace = state === 'replace'
  const FileButton = isReplace ? GreyTextButton : CTAButton
  const describedByProps =
    state === 'error' && errorId ? { 'aria-describedby': errorId } : {}

  const openFilePicker = (): void => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.target !== event.currentTarget) {
      return
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openFilePicker()
    }
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    if (!disabled) {
      setDragging(true)
    }
  }

  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    if (event.currentTarget.contains(event.relatedTarget as Node)) {
      return
    }
    setDragging(false)
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    setDragging(false)
    const [droppedFile] = event.dataTransfer.files
    if (!disabled && droppedFile) {
      onFilePick(droppedFile)
    }
  }

  const getTitle = (): string => {
    if (dragging) {
      return 'Yes! Drop your track file here'
    }
    if (isReplace) {
      return 'Drag & Drop another file to replace'
    }
    if (isTouchLayout) {
      return 'Tap to upload audio file'
    }
    return 'Drag & Drop track file to upload'
  }

  return (
    <Container
      role='button'
      tabIndex={disabled ? DISABLED_TAB_INDEX : 0}
      aria-disabled={disabled}
      aria-label={
        isReplace
          ? 'Replace the selected audio file'
          : 'Upload an audio file: drag and drop, or activate to browse'
      }
      aria-busy={loading}
      {...describedByProps}
      $dragging={dragging}
      $invalid={state === 'error'}
      onClick={openFilePicker}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <IconWrapper $dragging={dragging}>
        <UploadFolderIcon width={24} height={24} />
      </IconWrapper>
      <TextWrapper>
        <Title>{getTitle()}</Title>
        {!dragging && <Hint>{AUDIO_FILE_FORMATS_HINT}</Hint>}
      </TextWrapper>
      {!dragging && (
        <FileButton type='button' disabled={disabled} loading={loading}>
          {isReplace ? 'Replace file' : 'Browse file'}
        </FileButton>
      )}
      <HiddenInput
        ref={inputRef}
        type='file'
        accept={AUDIO_FILE_ACCEPT}
        disabled={disabled}
        onClick={(event) => event.stopPropagation()}
        onChange={(event) => {
          const selectedFile = event.target.files?.[0]
          if (selectedFile) {
            onFilePick(selectedFile)
          }
          event.target.value = ''
        }}
      />
    </Container>
  )
}
